import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { APP_CONFIG } from '../../config/appConfig';
import { toE164KR, genOtp6, sendOtpViaProxy, findEmailByPhoneE164, maskEmail } from '../../services/recoveryService';

function normalizePhone(raw) {
  return raw.replace(/[^0-9]/g, "");
}

function isTestNumber(phone) {
  const n = normalizePhone(phone);
  const { start, end } = APP_CONFIG.testRange;
  return n >= start && n <= end;
}

const FindIdPage = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const [testCode, setTestCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const [foundEmail, setFoundEmail] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const startTimer = () => {
    setTimer(180);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    const normalized = normalizePhone(phone);
    if (normalized.length < 10) {
      setError('올바른 전화번호를 입력해주세요.');
      return;
    }
    setBusy(true);
    setError('');

    if (isTestNumber(normalized)) {
      const otp = genOtp6();
      setTestCode(otp);
      setServerCode(otp);
      setCodeSent(true);
      startTimer();
      setBusy(false);
      return;
    }

    const otp = genOtp6();
    setServerCode(otp);

    try {
      await sendOtpViaProxy({ phone: normalized, authcode: otp });
      setCodeSent(true);
      startTimer();
    } catch (err) {
      setError(err.message || 'SMS 발송에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setError('인증번호를 입력해주세요.');
      return;
    }
    if (serverCode && code.trim() !== serverCode) {
      setError('인증번호가 일치하지 않습니다.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      const phoneE164 = toE164KR(phone);
      const email = await findEmailByPhoneE164(phoneE164);
      if (email) {
        setFoundEmail(maskEmail(email));
      } else {
        setError('해당 전화번호로 등록된 아이디가 없습니다.');
      }
    } catch (err) {
      console.error('아이디 찾기 실패:', err);
      setError('아이디 찾기에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (foundEmail) {
    return (
      <Container>
        <Title>아이디 찾기 결과</Title>
        <FormArea>
          <ResultBox>
            <ResultLabel>등록된 아이디</ResultLabel>
            <ResultValue>{foundEmail}</ResultValue>
          </ResultBox>
          <PrimaryBtn onClick={() => navigate('/login', { replace: true })}>
            로그인하러 가기
          </PrimaryBtn>
          <LinkBtn onClick={() => navigate('/find-password')}>
            비밀번호 찾기
          </LinkBtn>
        </FormArea>
      </Container>
    );
  }

  return (
    <Container>
      <Title>아이디 찾기</Title>
      <Desc>전화번호로 아이디를 찾을 수 있습니다.</Desc>

      <FormArea>
        <Label>전화번호</Label>
        <Input
          type="tel"
          placeholder=""
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={codeSent}
        />
        <SendBtn onClick={handleSendCode} disabled={busy || (codeSent && timer > 0)}>
          {codeSent ? '재전송' : '인증번호 전송'}
        </SendBtn>

        {codeSent && (
          <>
            <Label>인증번호 {timer > 0 && <TimerText>{formatTimer(timer)}</TimerText>}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder=""
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {testCode && <TestCodeBox>테스트 코드: {testCode}</TestCodeBox>}
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        {codeSent && (
          <PrimaryBtn onClick={handleVerify} disabled={busy}>
            {busy ? '확인 중...' : '아이디 찾기'}
          </PrimaryBtn>
        )}

        <LinkBtn onClick={() => navigate('/login')}>
          로그인으로 돌아가기
        </LinkBtn>
      </FormArea>
    </Container>
  );
};

export default FindIdPage;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #EEF2FF 0%, #F5F7FA 40%);
  padding: 80px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  font-size: 22px;
  text-align: center;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Desc = styled.p`
  font-size: 15px;
  color: #888;
  text-align: center;
  margin-bottom: 40px;
`;

const FormArea = styled.div`
  width: 100%;
  max-width: 480px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 20px;
  color: #333;
`;

const TimerText = styled.span`
  color: #4A7CFE;
  font-variant-numeric: tabular-nums;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  &:focus { border-color: #4A7CFE; outline: none; }
  &:disabled { background: #f5f5f5; }
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 12px;
  border-radius: 10px;
  background: #4A7CFE;
  color: white;
  font-size: 15px;
  &:disabled { opacity: 0.5; }
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border-radius: 28px;
  background: #4A7CFE;
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

const LinkBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;

const ResultBox = styled.div`
  background: #F0F4FF;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  margin: 24px 0;
`;

const ResultLabel = styled.p`
  font-size: 14px;
  color: #888;
  margin-bottom: 8px;
`;

const ResultValue = styled.p`
  font-size: 20px;
  color: #1a1a1a;
  letter-spacing: 0.5px;
`;

const TestCodeBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: #FFF3E0;
  border-radius: 10px;
  font-size: 14px;
  color: #E65100;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 14px;
  margin-top: 10px;
`;
